import React, { useCallback, useMemo } from 'react';
import { HeaderSection } from '@groupelacentrale/contact-storybook';
import { VehicleDescriptionItem } from 'types';
import { Search } from 'components/Search/Search';
import { keypressCallback } from 'utils';

export type VehicleDescriptionFamily = { label: string; vehicleDescriptionList: VehicleDescriptionItem[] };
export type VehicleDescriptionFamilyLabels = { label: string; options: string[] };

export type VehicleDescriptionCheckedItems = VehicleDescriptionItem | VehicleDescriptionFamily;
export type VehicleDescriptionUncheckedItems = string | VehicleDescriptionFamilyLabels;

export interface VehicleDescriptionFragmentProps {
  name: string;
  checkedElements: VehicleDescriptionCheckedItems[];
  handleCheck: (e: React.FormEvent<HTMLInputElement>) => void;
  handleKeyPressCheck: (e: React.KeyboardEvent) => void;
}

interface Props<T> {
  title: string;
  name: string;
  vehicleDescriptionList: T[];
  checkedElements: VehicleDescriptionCheckedItems[];
  uncheckedElements: VehicleDescriptionUncheckedItems[];
  uncheckedCounter: number;
  setVehicleDescriptionList: React.Dispatch<React.SetStateAction<T[]>>;
  Fragment: React.ComponentType<VehicleDescriptionFragmentProps>;
}

export function VehicleDescriptionFormViewSection<T extends VehicleDescriptionItem>({
  title,
  name,
  vehicleDescriptionList,
  checkedElements,
  uncheckedElements,
  uncheckedCounter,
  setVehicleDescriptionList,
  Fragment,
}: Props<T>): React.ReactElement<T> {
  const counter = useMemo(
    () => vehicleDescriptionList.reduce((counter, { selected }) => (selected ? counter + 1 : counter), 0),
    [vehicleDescriptionList],
  );

  const handleCheckValue = useCallback(
    (label: string, isChecked: boolean) => {
      const index = vehicleDescriptionList.findIndex(description => description.label === label);
      setVehicleDescriptionList(descriptionList => [
        ...descriptionList.slice(0, index),
        {
          ...descriptionList[index],
          selected: isChecked,
        },
        ...descriptionList.slice(index + 1),
      ]);
    },
    [vehicleDescriptionList, setVehicleDescriptionList],
  );

  const handleSelect = useCallback((option: string) => handleCheckValue(option, true), [handleCheckValue]);

  const handleCheck = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => handleCheckValue(e.currentTarget.value, e.currentTarget.checked),
    [handleCheckValue],
  );

  const handleKeyPressCheck = keypressCallback((option: string) => handleCheckValue(option, false), 'data-option');

  return (
    <div className="vehicle-description-section">
      <HeaderSection noUnderline>
        {title} <span className="description-counter">{counter}</span>
      </HeaderSection>
      <Search
        placeholder={`Rechercher parmis les ${uncheckedCounter} ${name}`}
        onSelect={handleSelect}
        searchCriteria="anywhere"
        options={uncheckedElements}
      />
      <div className="vehicle-description-list">
        <Fragment
          name={name}
          checkedElements={checkedElements}
          handleCheck={handleCheck}
          handleKeyPressCheck={handleKeyPressCheck}
        />
      </div>
    </div>
  );
}
